import { Fragment, type ReactNode } from 'react';
import { GitBranch } from 'lucide-react';
import { formatModelLabel } from '@reflex/shared';
import { AgentAssociationChips } from '@/components/agent/AgentAssociationChips';
import { RepoLabel } from '@reflex/ui/components/shared/RepoLabel';
import { useAgentRowFields } from '@/components/agent/useSessionView';
import type { AgentMetaLineProps } from '@/components/agent/agent-meta-line-props';

/** Subtle middot used to separate the meta-line segments. */
function MetaDot() {
  return (
    <span aria-hidden="true" className="shrink-0 text-muted-foreground/40">
      ·
    </span>
  );
}

/**
 * Compact, single-line agent metadata shown under a row title across an
 * agent's whole lifecycle (running, pinned, completed, archived). It surfaces:
 *
 *   - the agent type that ran (`claude-code`, `codex`, ...),
 *   - the model the run used, shortened via {@link formatModelLabel} so a full
 *     provider id (`claude-sonnet-4-20250514`) reads as `sonnet-4` and never
 *     overflows the row, when a model was pinned,
 *   - the repository the run targeted, as `organization/repo`, when a git-repo
 *     attachment is set,
 *   - the git branch from the `git-repo` attachment, when set,
 *   - the linked work items ({@link AgentAssociationChips}): PR badges first,
 *     then Linear / Jira issues and group chips, capped with a "+N" overflow.
 *
 * Segments are joined by a subtle middot. Everything except the PR badge reads
 * as muted context, matching the row chrome.
 *
 * The agent type, model, and repo segments are individually hideable from the
 * sidebar's view menu ({@link useAgentRowFields}): on a list where every run
 * uses the same agent against the same repo those segments are noise, and
 * dropping them gives the title and the linked items the width. Branch and
 * links stay: they differ per run, which is why they are on the line at all.
 * Because any segment can now be the first one, separators are inserted
 * between the segments that survive rather than baked into each one.
 */
export function AgentMetaLine({
  agentId,
  agentType,
  model,
  repoSlug: repoSlugInput,
  branch,
  associations,
}: AgentMetaLineProps) {
  const { fields } = useAgentRowFields();
  const repoSlug = repoSlugInput?.trim();
  const modelLabel = model?.trim() ? formatModelLabel(model.trim()) : null;
  const refs = associations ?? [];

  const segments: ReactNode[] = [];
  if (fields.agentType) {
    segments.push(
      <span key="agent" className="shrink-0 truncate" data-testid="agent-row-agent">
        {agentType}
      </span>,
    );
  }
  if (fields.model && modelLabel) {
    segments.push(
      <span key="model" className="min-w-0 truncate" data-testid="agent-row-model">
        {modelLabel}
      </span>,
    );
  }
  if (fields.repo && repoSlug) {
    segments.push(
      <RepoLabel key="repo" repo={repoSlug} className="min-w-0" data-testid="agent-row-repo" />,
    );
  }
  if (branch) {
    segments.push(
      <span key="branch" className="flex min-w-0 items-center gap-1" data-testid="agent-row-branch">
        <GitBranch className="icon-xs shrink-0" aria-hidden="true" />
        <span className="truncate">{branch}</span>
      </span>,
    );
  }
  if (refs.length > 0) {
    segments.push(
      // `display: contents` so the strip itself is the flex item, not this
      // click-guard wrapper. A `min-w-0` wrapper would let the flex item
      // shrink below the strip's own one-chip `minWidth` floor, so the
      // floored strip overflows the wrapper and is clipped by the row's
      // `overflow-hidden` column (the PR badge cut off on a narrow row).
      // As a direct flex item the strip keeps that floor and the muted
      // segments (model / repo / branch) truncate first — matching how the
      // stream header renders the same strip. The click guard still fires:
      // events bubble through the box-less wrapper to stop a chip click
      // from also opening the agent.
      <span key="links" className="contents" onClick={(e) => e.stopPropagation()}>
        {/* A meta line only renders on list rows (active / archived / draft),
            so verify each `open` PR lazily (on hover) instead of eagerly on
            mount. Eager verification is valuable on the focused stream header
            and detail identity — where one agent's stale-open state is worth a
            background fetch — but here a page of N agents would fire N
            `pulls/:number` fetches on load: the Sentry `AGENT-FLOW-WEB-4F`
            N+1. A lazy chip still corrects its state the instant the hover card
            opens, matching how merged/closed PRs already behave. */}
        <AgentAssociationChips associations={refs} agentId={agentId} eagerVerifyOpenPrs={false} />
      </span>,
    );
  }

  if (segments.length === 0) return null;

  return (
    <div
      className="mt-0.5 flex items-center gap-1.5 text-3xs text-muted-foreground leading-tight"
      data-testid="agent-row-meta"
    >
      {segments.map((segment, index) => (
        // Fragments carry no box, so the association strip stays a direct
        // flex child of the line and keeps its own width floor.
        <Fragment key={index}>
          {index > 0 ? <MetaDot /> : null}
          {segment}
        </Fragment>
      ))}
    </div>
  );
}
